import {useGetDrugsCategories} from '@/modules/basic-data/categories/api'
import {mapDrugsCategoriesToSelectItems} from '@/modules/basic-data/categories/functions'
import {ResourceListboxItem} from '@/shared/components/ui/resource-listbox-item'
import {Box, createListCollection, Listbox, SelectValueChangeDetails, Text} from '@chakra-ui/react'
import {DrugsCategory} from '@cypro/engine/schema'
import {withForm} from '@cypro/ui/integrations/form'
import {useStore} from '@tanstack/react-form'
import {useTranslate} from '@tolgee/react'

export const ClassificationFields = withForm({
  defaultValues: {categoriesIds: []} as {categoriesIds: string[]},
  render: function Render({form}) {
    const {t} = useTranslate('common')
    const {t: tSubstances} = useTranslate('substances')

    const {categories: allCategories} = useGetDrugsCategories()

    const selectedCategories = useStore(form.store, state => state.values.categoriesIds)

    const subcategoriesCollection = createListCollection({
      items: allCategories
        .filter(c => selectedCategories.includes(c.id))
        .flatMap(c => c.children.map(item => ({...item, value: item.id}))),
      groupBy: item => item.parentId || '',
    })
    const subcategoriesGroups = subcategoriesCollection.group()

    function onSubcategorySelect(changes: SelectValueChangeDetails<DrugsCategory>) {
      form.setFieldValue('categoriesIds', changes.value)
    }

    return (
      <>
        <form.AppField name="categoriesIds">
          {field => (
            <field.Select
              size="sm"
              label={t('category')}
              placeholder={tSubstances('category_placeholder')}
              items={mapDrugsCategoriesToSelectItems(allCategories)}
              multiple
            />
          )}
        </form.AppField>

        <Listbox.Root
          collection={subcategoriesCollection}
          value={selectedCategories}
          onValueChange={onSubcategorySelect}
          selectionMode="multiple"
          spaceY="2.5"
        >
          <Box>
            <Listbox.Label fontSize="sm" fontWeight="medium">
              {t('available_subcategories')}
            </Listbox.Label>
            {subcategoriesGroups.length > 0 && (
              <Text fontSize="sm" color="fg.subtle">
                {t('based_on_category_selection')}
              </Text>
            )}
            {!subcategoriesGroups.length && (
              <Text fontSize="sm" color="fg.subtle">
                {tSubstances('no_categories_selected')}
              </Text>
            )}
          </Box>

          {subcategoriesGroups.length > 0 && (
            <Listbox.Content>
              {subcategoriesGroups.map(([categoryId, subcategories]) => {
                const category = allCategories.find(c => c.id === categoryId)
                if (!category) return null

                return (
                  <Listbox.ItemGroup key={categoryId}>
                    <Listbox.ItemGroupLabel>{category.name}</Listbox.ItemGroupLabel>
                    {subcategories.map(subcategory => (
                      <ResourceListboxItem
                        key={subcategory.id}
                        label={subcategory.name}
                        description={subcategory.description}
                        status={subcategory.status}
                        item={subcategory}
                      />
                    ))}
                  </Listbox.ItemGroup>
                )
              })}
            </Listbox.Content>
          )}
        </Listbox.Root>
      </>
    )
  },
})
